import React, { useState } from "react";
import { Controller } from "react-hook-form";
import classnames from "classnames";
import {
  Box,
  Checkbox,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";

import { makeKey } from "../../../../utils/helpers";

import { useFieldsStyles } from "../fields.styles";

import { SelectFieldProps } from "./select-field.props";
import { SelectFieldChips } from "./select-field-chips/select-field-chips.component";
import { useSelectFieldStyles } from "./select-field.styles";
import { ToggleAll } from "./toggle-all/toggle-all.component";
import { withShowIf } from "../../../../components/with-show-if/with-show-If.component";

export const SelectField = withShowIf((props: SelectFieldProps) => {
  const { multiple = false, withToggleAll = false } = props;
  const [allChecked, setAllChecked] = useState<boolean>(false);

  const commonClasses = useFieldsStyles();
  const selectClasses = useSelectFieldStyles();

  const checkboxClasses = classnames({
    [selectClasses.checkbox]: true,
    [selectClasses.hidden]: !props.multiple,
  });

  const renderValue = !!props.multiple ? () => props.placeholder ?? "" : undefined;

  const hasError = !!props.form.formState.errors[props.name];
  const errorMessage = props.form.formState.errors[props.name]?.message;
  const canEdit = Boolean(props.userPermissions?.canEdit) || Boolean(props.noAuthRequired);

  const showToggleAll = () => multiple && withToggleAll;

  const handleToggleAll = (event: React.ChangeEvent<any>) => {
    const { checked } = event.target;
    const value = checked ? props.options.map((option) => option.value) : [];

    setAllChecked(checked);
    props.form.setValue(props.name, value);
  };

  const onChange = (event: React.ChangeEvent<{ name?: string; value: any }>) => {
    if (props.onChange) {
      props.onChange(event);
    }

    setAllChecked(event.target.value.length === props.options.length);
    return event;
  };

  return (
    <Controller
      control={props.form.control}
      name={`${props.name}` as const}
      render={(renderProps) => (
        <div className={commonClasses.fieldWrapper}>
          <Box display="flex" justifyContent="space-between">
            <FormLabel htmlFor={props.name} error={hasError} required={props.required}>
              {props.label}
            </FormLabel>
          </Box>
          <Select
            {...{ ...renderProps.field, ref: undefined }}
            disabled={!canEdit || props.disabled}
            displayEmpty={props.multiple}
            error={hasError}
            fullWidth
            inputRef={renderProps.field.ref}
            multiple={props.multiple}
            renderValue={renderValue}
            variant="outlined"
            onChange={(event) => renderProps.field.onChange(onChange(event))}
            startAdornment={
              showToggleAll() ? <ToggleAll onClick={handleToggleAll} checked={allChecked} /> : null
            }
            SelectDisplayProps={{ placeholder: `${props.placeholder}` }}
            MenuProps={{ title: `${props.label}` }}
            inputProps={{ title: `${props.label}`, id: `${props.name}` }}
            placeholder={props.placeholder}
          >
            {props.options.map((option, index) => (
              <MenuItem key={makeKey(props.name, index)} value={option.value}>
                <Checkbox
                  className={checkboxClasses}
                  checked={props.multiple && renderProps?.field.value?.indexOf(option.value) > -1}
                />
                <ListItemText className={selectClasses.option} primary={option.label} />
              </MenuItem>
            ))}
          </Select>

          <SelectFieldChips
            options={props.options}
            showIf={props.multiple}
            value={renderProps.field.value}
          />

          {hasError && <FormHelperText error={hasError}>{errorMessage}</FormHelperText>}
        </div>
      )}
    />
  );
});
